import React, { useState, useEffect, useMemo } from 'react';
import { 
  Mic, MicOff, FileText, Loader2, Square, Play, Pause, Zap, 
  RefreshCw, AlertCircle, Undo2, Save, RotateCcw, Check, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import HourglassIcon from './HourglassIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { QuickTemplates } from './QuickTemplates';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { SoftWarningChip } from '../../features/conflict-scanner';
import { useAutosizeTextarea } from '../../hooks/useAutosizeTextarea';
import SessionJumpNav from '../../features/session-divider/SessionJumpNav';
import axios from 'axios';

// Dotphrases — type these in the notes textarea, hit space to expand
export const DOT_PHRASES = {
  '.lowdoseCT': `[age] [gender] due for low dose lung CT per USPSTF recommendations. Criteria: Age 50-80, ≥20 pack-year smoking history, currently smokes or quit within past 15 years. Annual LDCT recommended. (Grade B)`,
  '.colonoscopy': `[age] [gender] due for colorectal cancer screening per USPSTF recommendations. Age 45-75, average risk. Options: Colonoscopy q10yr, FIT annually, FIT-DNA q1-3yr. (Grade A age 50-75, Grade B age 45-49)`,
  '.mammo': `[age] female due for breast cancer screening per USPSTF recommendations. Age 40-74, average risk. Biennial screening mammography recommended. (Grade B)`,
  '.pap': `[age] female due for cervical cancer screening per USPSTF recommendations. Pap q3yr (age 21-29), Pap q3yr OR hrHPV q5yr OR co-testing q5yr (age 30-65). (Grade A)`,
  '.psa': `[age] male — shared decision-making regarding prostate cancer screening per USPSTF recommendations. Age 55-69, discuss benefits/risks of PSA-based screening. (Grade C). Do not screen men ≥70 (Grade D).`,
  '.aaa': `[age] male with smoking history — one-time AAA screening per USPSTF recommendations. Men age 65-75 who have ever smoked. One-time screening ultrasonography. (Grade B)`,
  '.dm2': `Continue diabetes management. Last A1c: [value] on [date]. Current regimen: [meds]. Goal A1c <7%. Repeat A1c in 3 months. Annual diabetic eye exam, foot exam, microalbumin.`,
  '.htn': `Continue hypertension management. Current BP: [value]. Current regimen: [meds]. Goal BP <130/80 per ACC/AHA. BMP in [timeframe]. Lifestyle: DASH diet, sodium restriction, exercise.`,
  '.rtd': `Return to duty. No restrictions. Follow up PRN.`,
  '.qtrs': `Return to quarters x [24/48/72] hours. Light duty. No PT. Follow up if symptoms worsen or do not improve.`,
  '.cacs': `Referral to Radiology — Diagnostic Coronary Calcium Score. [age]yo [gender] w/ mixed HLD (TC [ ], LDL [ ]) not currently on statins. Please evaluate for onset of atherosclerosis per USPSTF recommendations. Risk stratification to guide shared decision-making regarding statin initiation.`,
  '.bhcry': `Patient was provided with active listening, reframing, choice recognition empowerment, and empathy. All questions were answered and patient agrees with plan.`,
  '.keyresults': `KEY RESULTS:\n- [Lab/Imaging Type]: [Value] ([Normal Range]) — [Clinical Significance]\n- [Lab/Imaging Type]: [Value] ([Normal Range]) — [Clinical Significance]\n\nIMPRESSION: Results reviewed and discussed with patient. See A/P for individual diagnosis plans.`,
  '.PEadd': `General Appearance:\n° Well-appearing. ° Well developed. ° Well nourished. ° In no acute distress. Oriented x 4.\nHead:\nInjuries: ° No evidence of a head injury.\nAppearance: ° Head normocephalic.\nLungs:\n° Respiration rhythm and depth was normal. ° Exaggerated use of accessory muscles for inspiration was not observed. ° Clear to auscultation. ° No wheezing was heard. ° No rhonchi were heard. ° No rales/crackles were heard.\nCardiovascular:\nHeart Rate And Rhythm: ° Normal.\nHeart Sounds: ° Normal S1 and S2. ° No gallop was heard. ° No click was heard. ° No pericardial friction rub heard.\nMurmurs: ° No murmurs were heard.\nArterial Pulses: ° Bilateral radial pulses were normal.\nEdema: ° Not present.\nCapillary Refill Test: ° A capillary refill test was normal.\nCarotid: no JVD, no bruit or thrill\nMusculoskeletal System:\nFunctional Exam:\nGeneral/bilateral: ° Mobility was not limited.\nNeck: Normal full ROM.\nBack: Normal full ROM.\nNeurological:\n° Oriented to time, place, and person. ° No impairment. Balance: ° Normal. ° Romberg's sign was absent.\nPsychiatric: Orientation: Person, place, time, and situation. Mood: ° Euthymic, appropriate.\nAffect: ° Normal, appropriate\nSkin:\n° Showed no ecchymosis. ° Temperature was normal. ° No skin lesions observed`,
  '.educac': `Purpose of CAC Scan:\nThe Coronary Artery Calcium (CAC) scan is a non-invasive test that uses computed tomography (CT) to detect and measure the amount of calcium in the coronary arteries. The purpose of the CAC scan is to:\n- Assess CHD Risk: The CAC scan can help assess the risk of coronary heart disease (CHD) in individuals who are not on statins.\n- Identify Asymptomatic Individuals: The CAC scan can help identify asymptomatic individuals who may be at risk for CHD.\n- Guide Treatment Decisions: The CAC scan can help guide treatment decisions, such as whether to start statin therapy or make lifestyle changes.\n- Monitor Progress: The CAC scan can help monitor progress over time and assess the effectiveness of treatment.\n\nInterpretation of CAC Scan Results:\nZero Calcium Score: No detectable calcium in the coronary arteries — suggests low risk of CHD.\nLow Calcium Score (1-100): Low to moderate risk of CHD.\nModerate Calcium Score (101-400): Moderate to high risk of CHD.\nHigh Calcium Score (401 or higher): High risk of CHD.\nOverall, the CAC scan is a useful tool for assessing CHD risk and guiding treatment decisions in individuals who are not on statins.`,
  '.eduMESA': `Coronary Age Score:\nThe Coronary Age Score is a measure of the age of the coronary arteries based on the CAC score. It is calculated using the Multi-Ethnic Study of Atherosclerosis (MESA) tool, which takes into account the CAC score, age, sex, and ethnicity. The Coronary Age Score is expressed as a percentage, with higher scores indicating a higher risk of CHD.`,
  '.eduhld': `The most effective way to increase HDL (good) cholesterol levels is through Regular Exercise, specifically Aerobic Exercise.\n\nStudies have consistently shown that aerobic exercise, such as brisk walking, cycling, or swimming, can increase HDL levels by 5-10% or more. The American Heart Association recommends at least 150 minutes of moderate-intensity aerobic exercise per week.\n\nMechanisms by which exercise increases HDL levels:\n- Stimulates the production of HDL in the liver\n- Enhances the clearance of triglycerides from the bloodstream\n- Improves insulin sensitivity, reducing triglyceride levels\n\nFoods to Increase HDL:\n- Fatty Fish: Salmon, tuna, mackerel (omega-3 fatty acids)\n- Avocados: Rich in monounsaturated and polyunsaturated fats\n- Nuts and Seeds: Almonds, walnuts, chia seeds\n- Olive Oil: Rich source of monounsaturated fats\n- Whole Grains: Brown rice, quinoa, whole-wheat bread\n- Fruits and Vegetables: Berries, leafy greens, bell peppers\n- Tea: Especially green tea (catechins)\n- Dark Chocolate: Contains flavonoids`,
  '.tbiPRA': `TBI/Concussion — DoD/VA TBICoE Progressive Return to Activity (PRA)\nCurrent PRA Stage: [1-6]\nNSI Score: [total]/88\nLast NSI Date:\nFollow-up in: [2-3 days]\n\n=== MEDPROS e-PROFILE TEMPLATE (DA PAM 40-502 Concussion) ===\nSoldier sustained concussion/mTBI on [date]. Currently PRA Stage [#].\n- No driving (personal, government, or tactical vehicles)\n- No weapons handling or range operations\n- No climbing or working at heights\n- No duties requiring sustained attention or complex decision-making\n- No combatives, contact sports, or collision activities\n- No field exercises or tactical operations\n- No alcohol consumption during recovery\n- Light duty only\n- If on sedating medication: impacts concentration and memory — no safety-sensitive duties\n- Follow-up with provider per TBICoE PRA protocol every 2-3 days\n- Duration: Until cleared through PRA Stage 6 or provider release\n\n=== PRA STAGES WITH COGNITIVE TIME LIMITS ===\n\n--- STAGE 1: RELATIVE REST (24-48h post-injury) ---\nPhysical: NO exercise or PT of any kind\nCognitive: MINIMAL — no video games, no screens, no driving\n- No phone conversations beyond brief check-ins\n- No reading beyond very light/short duration\n- NO complex decisions or leadership duties\n- NO recurring loud noises (range, motor pool, flight line)\n- Be mindful of visual disturbances, blurred vision, light sensitivity\n- Encourage: rest, sleep 8+ hrs, hydration, quiet dark environment\nAdvance: Asymptomatic 24h → Stage 2\n\n--- STAGE 2-3: LIGHT TO MODERATE ACTIVITY ---\nPhysical: Walking, elliptical, stationary bike (Stage 2); jogging, push-ups, carry weight (Stage 3)\nCognitive: <30 MINUTES activity, then ≥60 MINUTES rest before next session\n- Phone calls: keep under 30 min, take breaks\n- Screen time: <30 min sessions, stop if headache/eye strain\n- NO video games\n- NO driving (Stage 2); NO tactical vehicles (Stage 3)\n- NO combatives or contact sports\n- NO alcohol\n- NO lifting >20 lbs (Stage 2)\nAdvance: Asymptomatic 24h at each stage\n\n--- STAGE 4-5: PROGRESSIVE TO INTENSIVE ---\nPhysical: Non-contact sports, full resistance (Stage 4); usual exercise, weapons sim, NVG/helmet/pack (Stage 5)\nCognitive: <50 MINUTES activity including problem-solving, multitasking, then rest\n- Phone/screen time: <50 min sessions\n- Driving: supervised only (Stage 4); as appropriate (Stage 5)\n- NO combatives or full contact (Stage 4); supervised contact OK (Stage 5)\n- NO alcohol\n- Stage 5: Must pass Physical RTD (2min aerobic RPE 16+, asymptomatic) + Cognitive RTD (ANAM)\n- Minimum 24h in Stage 5 before RTD\nAdvance: Pass both screenings → Stage 6\n\n--- STAGE 6: RETURN TO DUTY ---\nFull unrestricted activity. All duties, driving, weapons, combatives resume.\n- 2nd concussion in 12mo: min 7 days total PRA\n- 3+ in 12mo: STOP at Stage 5, TBI specialist clearance ONLY\n\nREGRESSION: Symptoms worsen → regress to prior stage, rest 24h. No progress 3 days at same stage → refer specialty.\nNSI: Reassess q2-3 days. Persistent >7 days → VA/DoD Module B.\nREFERRAL TRIGGERS: No progress 3 days, symptoms >15 days, ≥3 mTBIs/12mo, new neuro deficit, behavioral changes.\nRED FLAGS → ER: Worsening headache, repeated vomiting, seizure, one-sided weakness/numbness, slurred speech, confusion, clear fluid from nose/ear.`,
  '.tbiNSI': `NEUROBEHAVIORAL SYMPTOM INVENTORY (NSI)\nRate each symptom 0-4 over the past 2 weeks:\n0 = None (rarely present, not a problem)\n1 = Mild (occasional, doesn't disrupt daily life)\n2 = Moderate (often present, some disruption, requires effort)\n3 = Severe (frequent disruption, need help with simple tasks)\n4 = Very Severe (almost always present, unable to function)\n\nVESTIBULAR / BALANCE:\n1. Feeling dizzy: ___\n2. Loss of balance: ___\n3. Poor coordination, clumsy: ___\n\nPAIN / SOMATIC:\n4. Headaches: ___\n5. Nausea: ___\n\nSENSORY:\n6. Vision problems (blurring, trouble seeing): ___\n7. Sensitivity to light: ___\n8. Hearing difficulty: ___\n9. Sensitivity to noise: ___\n10. Numbness or tingling on parts of body: ___\n11. Change in taste or smell: ___\n12. Loss of appetite or increased appetite: ___\n\nCOGNITIVE:\n13. Poor concentration (can't pay attention, easily distracted): ___\n14. Forgetfulness (can't remember things): ___\n15. Difficulty making decisions: ___\n16. Slowed thinking (can't get organized, can't finish things): ___\n\nENERGY / SLEEP:\n17. Fatigue (loss of energy, getting tired easily): ___\n18. Difficulty falling or staying asleep: ___\n\nMOOD / EMOTIONAL:\n19. Feeling anxious or tense: ___\n20. Feeling depressed or sad: ___\n21. Irritability (easily annoyed): ___\n22. Poor frustration tolerance (feeling easily overwhelmed): ___\n\nTOTAL SCORE: ___ / 88\n\nSCORING GUIDE:\n0-10: Minimal symptoms\n11-22: Mild — monitor, may advance PRA stage\n23-44: Moderate — hold current PRA stage, reassess in 2-3 days\n45-66: Severe — consider regression, targeted interventions\n67-88: Very Severe — specialty referral, VA/DoD Module B\n\nWATCH FOR (tell your soldier):\n- Worsening headache that doesn't go away\n- Dizziness or balance problems when standing/walking\n- Blurred or double vision\n- Can't concentrate or remember new information\n- Feeling more irritable, anxious, or sad than usual\n- Trouble sleeping or sleeping too much\n- Nausea or vomiting\n- Sensitivity to light or loud noise\n- Numbness or tingling\n- If ANY of these get WORSE instead of better → come back immediately\n- RED FLAGS → ER: Seizure, repeated vomiting, one-sided weakness, slurred speech, confusion, clear fluid from nose/ear`,
};

/**
 * NotesInput - Main text input area with recording controls
 * Extracted from App.js for maintainability
 * 
 * SAFETY FEATURES (March 2026):
 * - Recover Lost Notes button (retrieves from localStorage)
 * - Safer Pause/Resume UI (prevents accidental overwrite)
 * - Confirmation dialog after AI Enhance
 */
export const NotesInput = ({
  effectiveRole,
  notes,
  setNotes,
  recording,
  notesHistory,
  undoNotes,
  onSaveTranscription,
  onGenerateDocs,
  isGenerating,
  onSmartPaste, // Feb 16 UX #5 — paste auto-extract demographics
}) => {
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveredNotes, setRecoveredNotes] = useState('');
  const [showTranscriptionConfirm, setShowTranscriptionConfirm] = useState(false);
  const [pendingTranscription, setPendingTranscription] = useState('');
  const [customPhrases, setCustomPhrases] = useState({});

  // Feb 18, 2026 — Dot-phrase autocomplete state. As the provider types a
  // token that starts with ".", we show a small floating suggestion menu
  // anchored to the textarea. Arrow keys navigate, Enter/Tab selects, Escape
  // dismisses. Selecting expands the phrase inline (no need to type the
  // trailing space anymore).
  const [autocomplete, setAutocomplete] = useState({
    open: false,
    matches: [],        // [{ key, content, source }]
    selectedIdx: 0,
    triggerWord: '',    // the partial token, e.g. ".pe"
    triggerStart: 0,    // index in textarea where the token begins
  });
  // Offline-aware mic: Deepgram needs network. When offline, disable the mic
  // button with a friendly tooltip explaining why (Feb 16, 2026 — Lean Offline
  // Mode MVP).
  const { status: connectionStatus } = useConnectionStatus();
  const isOffline = connectionStatus === 'offline';

  // Feb 26, 2026 — Universal textarea auto-expand. Works in ALL mood themes
  // (not just high-contrast). Recomputes height on `notes` change so long
  // dictations don't get clipped into a tiny scroll-window. Caps at maxRows
  // to keep the page navigable; beyond that, internal scroll kicks in.
  const recomputeTextareaHeight = useAutosizeTextarea(
    recording.notesTextareaRef,
    notes,
    { minRows: effectiveRole === 'patient' ? 18 : 22, maxRows: 60 }
  );

  // Load custom dot phrases
  useEffect(() => {
    const API = window.__WR_API_BASE__;
    axios.get(`${API}/api/dot-phrases`, { withCredentials: true })
      .then(res => {
        const map = {};
        (res.data.phrases || []).forEach(p => { map[p.keyword] = p.content; });
        setCustomPhrases(map);
      })
      .catch(() => {});
  }, []);
  
  // Attempt to recover lost notes from localStorage or IndexedDB audio
  const attemptRecovery = async () => {
    try {
      // First: check localStorage for text notes
      const autosave = localStorage.getItem('writeright_autosave');
      if (autosave) {
        const data = JSON.parse(autosave);
        if (data.notes && data.notes.trim()) {
          setRecoveredNotes(data.notes);
          setShowRecoveryModal(true);
          return;
        }
      }
      // Second: check IndexedDB for saved audio blob
      const { loadAudioBlob } = await import('../../hooks/audioStorage');
      const saved = await loadAudioBlob();
      if (saved && saved.blob) {
        toast.info('Found saved audio recording! Click "Enhance with AI" to re-transcribe.', { duration: 6000 });
        return;
      }
      toast.info('No saved notes found in backup storage.', { duration: 3000 });
    } catch (e) {
      toast.error('Could not access backup storage.', { duration: 3000 });
    }
  };
  
  // Restore recovered notes
  const restoreRecoveredNotes = () => {
    if (recoveredNotes) {
      // If there are existing notes, append; otherwise replace
      if (notes.trim()) {
        setNotes(notes + '\n\n--- RECOVERED ---\n' + recoveredNotes);
      } else {
        setNotes(recoveredNotes);
      }
      toast.success('Notes recovered!', { duration: 2000 });
      setShowRecoveryModal(false);
      setRecoveredNotes('');
    }
  };
  return (
    <div className="space-y-3">
      {/* Recovery Modal */}
      <Dialog open={showRecoveryModal} onOpenChange={setShowRecoveryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-500" />
              Recovered Notes Found!
            </DialogTitle>
            <DialogDescription>
              Found backup notes in storage. Preview below:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto p-3 bg-muted/50 rounded-lg border text-sm whitespace-pre-wrap">
            {recoveredNotes || 'No content'}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRecoveryModal(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={restoreRecoveredNotes} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-1" /> Restore These Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Notes Label with Undo button and Recording status */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {effectiveRole === 'patient' ? 'Describe Your Situation' : 'Notes'}
          </Label>
          {effectiveRole === 'provider' && (
            <QuickTemplates onApplyTemplate={(text) => setNotes(text)} notes={notes} />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Recording Status Badge - More visible */}
          {recording.isRecording && (
            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              recording.isPaused ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
            }`}>
              <span className={`w-2 h-2 rounded-full ${recording.isPaused ? 'bg-amber-400' : 'bg-red-500 animate-pulse'}`}></span>
              {recording.isPaused ? 'PAUSED' : 'RECORDING'}
              <span className="font-mono ml-1">
                {recording.recordingDurationFormatted || '0:00'}
              </span>
            </span>
          )}
          {/* Screen-lock prevention chip — Feb 26, 2026 confirmation indicator. */}
          {recording.screenLockActive && (
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              title="Screen-lock prevention is active — your device will not auto-sleep while recording/transcribing"
              data-testid="screen-lock-active-chip"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              SCREEN AWAKE
            </span>
          )}
          {notesHistory.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={undoNotes}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              title={`Undo (${notesHistory.length} saved)`}
            >
              <Undo2 className="w-3 h-3 mr-1" />
              Undo ({notesHistory.length})
            </Button>
          )}
        </div>
      </div>
      
      {/* Microphone Button — above textarea */}
      <div className="flex flex-col items-center gap-2 py-2">
        {/* Recording controls when active - SAFER UI */}
        {recording.isRecording && (
          <div className={`flex flex-col items-center gap-3 mb-2 p-4 rounded-xl border-2 ${
            recording.isPaused 
              ? 'bg-amber-500/10 border-amber-500/50' 
              : 'bg-red-500/10 border-red-500/50'
          }`}>
            {/* Status display */}
            <div className="flex items-center gap-3">
              <span className={`text-lg font-bold ${recording.isPaused ? 'text-amber-400' : 'text-red-400 animate-pulse'}`}>
                {recording.isPaused ? '⏸ PAUSED' : '● RECORDING'}
              </span>
              <span className="text-muted-foreground text-lg font-mono bg-muted/50 px-3 py-1 rounded-lg">
                {recording.recordingDurationFormatted || '0:00'}
              </span>
            </div>
            
            {/* Audio level indicator when recording */}
            {!recording.isPaused && recording.audioLevel !== undefined && (
              <div className="w-full max-w-[200px] h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-100"
                  style={{ width: `${Math.min(100, recording.audioLevel || 0)}%` }}
                />
              </div>
            )}
            
            {/* PAUSE/RESUME - Big prominent button when paused */}
            {recording.isPaused ? (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={recording.resumeRecording}
                  className="px-8 py-3 rounded-full text-base font-bold bg-green-500 hover:bg-green-400 text-white shadow-lg transition-all transform hover:scale-105"
                  data-testid="resume-recording-btn"
                >
                  <Play className="w-5 h-5 inline mr-2" />
                  RESUME RECORDING
                </button>
                <p className="text-xs text-muted-foreground">Recording is paused - tap above to continue</p>
              </div>
            ) : (
              <button
                onClick={recording.pauseRecording}
                className="px-5 py-2 rounded-full text-sm font-medium bg-amber-500 hover:bg-amber-400 text-white transition-all"
                data-testid="pause-recording-btn"
              >
                <Pause className="w-4 h-4 inline mr-1" />
                Pause
              </button>
            )}
          </div>
        )}
        
        {/* Main Mic Button - HIDDEN when paused to prevent accidental overwrite */}
        {(!recording.isRecording || !recording.isPaused) && (
          <button 
            onClick={recording.isRecording ? recording.stopRecording : recording.startRecording} 
            disabled={recording.isTranscribing || isOffline} 
            className={`rounded-full flex items-center justify-center transition-all shadow-lg ${
              recording.isRecording 
                ? 'bg-red-500 hover:bg-red-600 ring-4 ring-red-500/30' 
                : isOffline
                  ? 'bg-slate-600 cursor-not-allowed opacity-60'
                  : 'bg-amber-500 hover:bg-amber-400'
            } ${recording.isTranscribing ? 'opacity-50' : ''}`} 
            style={{ width: '56px', height: '56px', minWidth: '56px', minHeight: '56px' }}
            data-testid="main-record-button"
            data-offline={isOffline ? 'true' : undefined}
            title={isOffline
              ? 'Dictation paused — needs internet. Type notes manually; your draft auto-saves.'
              : recording.isRecording ? 'Stop & Transcribe' : 'Click to record'}
          >
            {recording.isTranscribing ? (
              <Loader2 className="w-7 h-7 text-slate-900 animate-spin" />
            ) : recording.isRecording ? (
              <Square className="w-6 h-6 text-white" />
            ) : isOffline ? (
              <MicOff className="w-7 h-7 text-slate-300" />
            ) : (
              <Mic className="w-7 h-7 text-slate-900" />
            )}
          </button>
        )}
        
        {/* Stop Recording button when paused (separate from mic button) */}
        {recording.isRecording && recording.isPaused && (
          <button
            onClick={recording.stopRecording}
            className="mt-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 transition-all"
            data-testid="stop-recording-paused-btn"
          >
            <Square className="w-4 h-4 inline mr-1" />
            Stop & Save Recording
          </button>
        )}
        
        {/* Helper text */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {recording.isTranscribing ? 'Transcribing...' : recording.isRecording ? (recording.isPaused ? 'Paused - resume or stop above' : 'Speaking... text appears in notes below') : 'Click to record (appends to notes)'}
          </p>
          {!recording.isRecording && (
            <p className="text-[10px] text-muted-foreground/60">Max ~20-25 minutes per recording</p>
          )}
        </div>
        
        {/* Live transcript preview - shows what's being heard in real-time */}
        {recording.isRecording && !recording.isPaused && recording.liveTranscript && (
          <div className="mt-2 p-2 bg-secondary/50 border border-border rounded-lg max-w-full">
            <p className="text-[10px] text-muted-foreground mb-1">Hearing:</p>
            <p className="text-xs text-foreground/80 italic truncate">"{recording.liveTranscript}"</p>
          </div>
        )}
      </div>
      
      {/* The text area - ALWAYS resizable, bigger minimum height */}
      <div className="relative">
        <Textarea 
          ref={recording.notesTextareaRef}
          placeholder={effectiveRole === 'patient' 
            ? "Describe your situation and what letter you need..." 
            : "Type notes here, or use the mic button above to dictate..."
          } 
          value={notes} 
          onKeyDown={(e) => {
            // Mar 3, 2026 — Ctrl/Cmd+Enter fires Generate (last keyboard-only
            // gap in the provider hot path). Plain Enter still inserts a
            // newline like normal. Preserved before autocomplete guard so it
            // works even while the dot-phrase menu is closed.
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
              if (onGenerateDocs && (notes || '').trim().length > 0) {
                e.preventDefault();
                try { toast.success('Generating…', { duration: 1200 }); } catch (_) { /* ignore */ }
                onGenerateDocs();
                return;
              }
            }
            // Dot-phrase autocomplete keyboard navigation
            if (!autocomplete.open || autocomplete.matches.length === 0) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setAutocomplete(prev => ({ ...prev, selectedIdx: (prev.selectedIdx + 1) % prev.matches.length }));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setAutocomplete(prev => ({
                ...prev,
                selectedIdx: (prev.selectedIdx - 1 + prev.matches.length) % prev.matches.length,
              }));
            } else if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              const match = autocomplete.matches[autocomplete.selectedIdx];
              if (match) {
                const before = notes.slice(0, autocomplete.triggerStart);
                const after = notes.slice(autocomplete.triggerStart + autocomplete.triggerWord.length);
                const next = before + match.content + (after.startsWith(' ') ? '' : ' ') + after;
                setNotes(next);
                toast.success(`Inserted ${match.key}`, { duration: 1500 });
              }
              setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
            }
          }}
          onChange={(e) => {
            const val = e.target.value;
            // Dotphrase expansion on space-trigger.
            // Forgiving match: try (1) exact, (2) lowercase, (3) with dot prefix,
            // (4) lowercase + dot prefix — covers typos like "peadd" "PEadd" ".peadd" "PEadd".
            if (val.endsWith(' ')) {
              const words = val.trimEnd().split(/\s+/);
              const lastWord = words[words.length - 1];
              if (lastWord) {
                const allPhrases = { ...DOT_PHRASES, ...customPhrases };
                // Build a lowercase lookup map once per call (not per-keystroke during typing)
                const lcMap = {};
                Object.keys(allPhrases).forEach((k) => { lcMap[k.toLowerCase()] = allPhrases[k]; });
                const candidates = [
                  lastWord,                                  // exact
                  lastWord.toLowerCase(),                    // case-insensitive
                  lastWord.startsWith('.') ? lastWord : '.' + lastWord, // missing dot
                  '.' + lastWord.replace(/^\./, '').toLowerCase(),       // missing dot + case
                ];
                let expansion = null;
                for (const c of candidates) {
                  if (allPhrases[c]) { expansion = allPhrases[c]; break; }
                  if (lcMap[c.toLowerCase()]) { expansion = lcMap[c.toLowerCase()]; break; }
                }
                if (expansion) {
                  const before = val.substring(0, val.lastIndexOf(lastWord));
                  setNotes(before + expansion + ' ');
                  toast.success(`Expanded ${lastWord}`, { duration: 1500 });
                  setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
                  return;
                }
              }
            }
            setNotes(val);

            // Feb 18, 2026 — Live dot-phrase autocomplete. Detect a partial
            // ".tok" token at the caret position. Show matches; allow
            // arrow-keys + Enter/Tab to select.
            try {
              const caret = e.target.selectionStart ?? val.length;
              // Walk backwards from caret to find the start of the current word.
              let start = caret;
              while (start > 0 && !/\s/.test(val[start - 1])) start--;
              const token = val.slice(start, caret);
              if (token.startsWith('.') && token.length >= 1) {
                const allPhrases = { ...DOT_PHRASES, ...customPhrases };
                const tokLc = token.toLowerCase();
                // Two-tier match: prefix-match first (sorted on top), then substring.
                const entries = Object.entries(allPhrases).map(([k, content]) => ({ key: k, content }));
                const prefixMatches = entries.filter(e => e.key.toLowerCase().startsWith(tokLc));
                const subMatches = entries.filter(e =>
                  !e.key.toLowerCase().startsWith(tokLc) && e.key.toLowerCase().includes(tokLc.replace(/^\./, ''))
                );
                const matches = [...prefixMatches, ...subMatches].slice(0, 8);
                if (matches.length > 0) {
                  setAutocomplete({
                    open: true,
                    matches,
                    selectedIdx: 0,
                    triggerWord: token,
                    triggerStart: start,
                  });
                } else {
                  setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
                }
              } else if (autocomplete.open) {
                setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
              }
            } catch (_) { /* non-fatal */ }
          }}
          onBlur={() => {
            // Close the menu after the click handler on a suggestion has a
            // chance to fire. 150ms is enough for onMouseDown to land.
            setTimeout(() => setAutocomplete(prev => ({ ...prev, open: false })), 150);
          }}
          className={`text-foreground bg-input border-border text-sm ${effectiveRole === 'patient' ? 'min-h-[280px]' : 'min-h-[300px]'}`}
          style={{ resize: 'vertical' }}
          data-testid="notes-textarea"
          spellCheck={true}
          onPaste={(e) => {
            // Smart paste: when the provider pastes a large chunk (typically a
            // Genesis chart blob), try to auto-extract demographics. Feb 16,
            // 2026 — UX improvement #5. Caller wires up the props.
            try {
              if (!onSmartPaste) return;
              const text = e.clipboardData?.getData('text') || '';
              const extracted = onSmartPaste(text);
              if (extracted) {
                toast.success(
                  `Auto-extracted ${Object.keys(extracted).join(', ')} from paste`,
                  { duration: 3000 }
                );
              }
            } catch { /* swallow */ }
          }}
        />

        {/* Dot-phrase autocomplete dropdown — anchored top-right of the
            textarea so it doesn't cover what the provider is typing. */}
        {autocomplete.open && autocomplete.matches.length > 0 && (
          <div
            className="absolute right-2 top-2 z-30 w-80 max-h-80 overflow-y-auto rounded-lg border border-amber-500/40 bg-card/95 backdrop-blur-sm shadow-xl"
            data-testid="dotphrase-autocomplete"
          >
            <div className="px-2 py-1 border-b border-border bg-amber-500/10 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                Dot-phrase suggestions
              </span>
              <span className="text-[9px] text-muted-foreground">↑↓ navigate · Enter / Tab insert · Esc close</span>
            </div>
            <ul className="py-1">
              {autocomplete.matches.map((match, idx) => {
                const preview = (match.content || '').replace(/\s+/g, ' ').slice(0, 90);
                const isSelected = idx === autocomplete.selectedIdx;
                return (
                  <li
                    key={match.key}
                    data-testid={`dotphrase-suggestion-${match.key}`}
                    onMouseDown={(e) => {
                      // Prevent textarea blur from firing before click
                      e.preventDefault();
                      const before = notes.slice(0, autocomplete.triggerStart);
                      const after = notes.slice(autocomplete.triggerStart + autocomplete.triggerWord.length);
                      const next = before + match.content + (after.startsWith(' ') ? '' : ' ') + after;
                      setNotes(next);
                      toast.success(`Inserted ${match.key}`, { duration: 1500 });
                      setAutocomplete({ open: false, matches: [], selectedIdx: 0, triggerWord: '', triggerStart: 0 });
                    }}
                    onMouseEnter={() => setAutocomplete(prev => ({ ...prev, selectedIdx: idx }))}
                    className={`px-2 py-1.5 cursor-pointer transition-colors ${
                      isSelected ? 'bg-amber-500/20 border-l-2 border-amber-400' : 'hover:bg-secondary/40 border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-bold ${isSelected ? 'text-amber-200' : 'text-foreground'}`}>
                        {match.key}
                      </span>
                      {customPhrases[match.key] && (
                        <span className="text-[8px] uppercase tracking-wider text-sky-400 bg-sky-500/15 border border-sky-500/30 px-1 py-0.5 rounded">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className={`text-[10px] mt-0.5 line-clamp-2 ${isSelected ? 'text-amber-100/80' : 'text-muted-foreground'}`}>
                      {preview}{(match.content || '').length > 90 ? '…' : ''}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {/* Action bar below textarea: Recover (left), Create Docs (right) */}
      <div className="flex items-center justify-between px-0.5 -mt-1">
        <button
          onClick={attemptRecovery}
          className="text-[10px] text-muted-foreground/40 hover:text-amber-400 transition-colors flex items-center gap-1 py-0.5"
          title="Try to recover lost notes from backup"
          data-testid="recover-notes-btn"
        >
          <RotateCcw className="w-2.5 h-2.5" />
          Recover
        </button>
        {notes.trim() && onGenerateDocs && (
          <Button
            onClick={onGenerateDocs}
            disabled={isGenerating || !notes?.trim()}
            size="sm"
            className="h-7 px-3 text-xs bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
            data-testid="create-docs-textarea-btn"
            title={isGenerating ? "Generating..." : "Generate  (⌘/Ctrl + Enter)"}
          >
            {isGenerating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <img src="/emblem_icon.png" alt="" className="w-4 h-4 object-contain" />
            )}
          </Button>
        )}
      </div>

      {/* Session Jump Navigator — only renders when 2+ session dividers exist */}
      <SessionJumpNav notes={notes} textareaRef={recording.notesTextareaRef} />

      {/* Soft Warnings — non-blocking advisory chip (Feb 26, 2026).
          One subtle line, only visible when the scanner finds something.
          Hover-to-expand; no modal. */}
      <SoftWarningChip notes={notes} />

      {/* Transcription Error */}
      {recording.transcriptionError && (
        <div className="p-2 bg-destructive/10 border border-destructive/30 rounded-lg text-xs">
          <p className="text-destructive mb-2">{recording.transcriptionError}</p>
          <div className="flex gap-2">
            {recording.lastFailedAudio && (
              <Button variant="outline" size="sm" onClick={recording.retryTranscription} disabled={recording.isTranscribing} className="h-6 text-[10px]">
                <RefreshCw className="w-3 h-3 mr-1" />Retry
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={recording.clearTranscriptionError} className="h-6 text-[10px]">Dismiss</Button>
          </div>
        </div>
      )}
      
      {/* Recovery banner — shows after recording when there's saved audio.
          Feb 28, 2026: bumped to a prominent, full-width amber banner
          (was a small secondary button) — provider reported missing it on
          mobile after 11-min recordings whose upload was killed by tab
          backgrounding. Now visually impossible to miss, with size+duration
          hint and a one-tap "Transcribe Now" CTA. */}
      {!recording.isRecording && !recording.isTranscribing && recording.lastFailedAudio && !recording.transcriptionError && (
        <div
          className="flex flex-col gap-2 p-3 rounded-lg border-2 border-amber-400/50 bg-amber-500/10 shadow-md"
          data-testid="recording-recovery-banner"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base">🎙️</span>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-amber-200">
                  Recording saved — needs transcription
                </span>
                <span className="text-[11px] text-amber-200/70">
                  {(recording.lastFailedAudio.size / (1024 * 1024)).toFixed(1)} MB
                  {' · '}
                  ~{Math.max(1, Math.round(recording.lastFailedAudio.size / (1024 * 1024) * 60))} sec audio
                </span>
              </div>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={async () => {
                await recording.enhanceWithAI();
                setTimeout(() => {
                  const currentNotes = document.querySelector('[data-testid="notes-textarea"]')?.value;
                  if (currentNotes && currentNotes.trim().length > 10) {
                    const autosaveData = {
                      notes: currentNotes,
                      savedAt: new Date().toISOString(),
                      source: 'ai_transcription'
                    };
                    localStorage.setItem('writeright_autosave', JSON.stringify(autosaveData));
                    toast.success('Transcription saved.', { duration: 2000 });
                  }
                }, 1000);
              }}
              className="h-9 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-amber-950 shrink-0"
              data-testid="enhance-with-ai-btn"
            >
              <Zap className="w-3.5 h-3.5 mr-1" />
              Transcribe Now
            </Button>
          </div>
          <p className="text-[10px] text-amber-200/60 leading-snug">
            Tap "Transcribe Now" to send to Whisper/Deepgram. Audio is held
            in local storage until you transcribe or start a new recording.
          </p>
        </div>
      )}
    </div>
  );
};


export default NotesInput;
